import { apiRequest, ApiError } from '@/domains/api';
import type {
  IRankingAddress,
  IRankingHistory,
  IRankingLists,
  IRankingPlacement,
  ISetPlacementBody,
  IParticipantTerritories,
  ITerritoryOverrideHistory
} from './ranking-contracts';

/**
 * survey-api's rankings (SR-222 block 6, `RankingEndpoints.cs`): a survey's list catalogue, a
 * participant's placements, one placement's write, removal and history. The sole reader of
 * `/api/v1/admin/surveys/{code}/ranking-lists` and `/api/v1/admin/participants/{id}/rankings*`.
 *
 * **A survey's catalogue is read once per page load** (`lists`) — the staff-field schema's rule
 * and its reasons (`internal-fields-api.ts`): it changes only with survey-api's code, every part
 * drawing one survey asks for it, so one module-level promise per code answers them all, dropped
 * when it fails and taking no `AbortSignal`. It names **the survey the placements are kept under**
 * (`keptUnder` — served by survey-api `30d88f37`): `CANADA_FR` answers `CANADA`'s lists, and
 * a write is addressed by it. **A 404 answers `null`**, no catalogue: survey-api answers it for a
 * code that is none of its six surveys, which keeps no rankings — not a failure to say. Every
 * other refusal is, and so is a 2xx without `lists` or `keptUnder`, refused inside the shared
 * promise so it is forgotten like any failure — a catalogue without `keptUnder` would leave a
 * write to guess its survey. Rejected: a mirror of `RankingCatalogue.cs` (the roadmap's B1, served
 * — the wire names the lists and which are territorial).
 *
 * **A 2xx that carries no placements is a failed read**, and so is a write's 200 without a
 * placement and a history without its `items` or with a `placement` neither an object nor null —
 * figures' rule (`figures-api.ts`): the hub draws from them and holds a write's answer over the
 * tables, and this app has no error boundary. What is inside the lists is not judged.
 *
 * **Every segment of an address is encoded as one segment.** Its parts come from survey-api's own
 * documents (the hub's ids, the catalogue's list codes) or from a year typed on the page, and
 * `encodeURIComponent` leaves `.` alone — so a year typed `.` or `..` would be removed by the URL
 * parser behind `fetch` and reach another route. Such a segment is refused before the request, the
 * `internal-fields-api.ts` guard.
 *
 * **No `If-Match`, no `ETag`.** A placement is last-write-wins on survey-api's side (the
 * roadmap's B2, a ticket of its own).
 */

const BASE = '/api/v1/admin';

/** A placement's history is a popover's, ten a page — survey-api's default (50) is a list's. */
const HISTORY_PAGE_SIZE = 10;

/** Values that could only reach a different route — see the module's comment. */
const NOT_A_SEGMENT = new Set(['', '.', '..']);

/** Each survey's catalogue read this page load, by code — see the module's comment. */
const listReads = new Map<string, Promise<Nullable<IRankingLists>>>();

/** Whether `value` can be sent as one segment of an address — a year typed `.` or `..` cannot,
 *  and the page says so itself rather than as a request that failed. */
export function isSendableSegment(value: string): boolean {
  return !NOT_A_SEGMENT.has(value);
}

/** `value` encoded as one segment of an address — refused where it could only reach another
 *  route. Shared with `ranking-process-api.ts`. */
export function segment(value: string): string {
  if (!isSendableSegment(value)) {
    throw new Error(`'${value}' cannot be sent as a path segment`);
  }
  return encodeURIComponent(value);
}

export function isObject(answer: unknown): answer is Record<string, unknown> {
  return typeof answer === 'object' && answer !== null;
}

function listsOf(answer: unknown): IRankingLists {
  if (!isObject(answer) || typeof answer.keptUnder !== 'string' || !Array.isArray(answer.lists)) {
    throw new Error('The answer held no ranking lists');
  }
  return answer as unknown as IRankingLists;
}

function placementsOf(answer: unknown): IRankingPlacement[] {
  if (!isObject(answer) || !Array.isArray(answer.placements)) {
    throw new Error('The answer held no placements');
  }
  return answer.placements as IRankingPlacement[];
}

function placementOf(answer: unknown): IRankingPlacement {
  if (!isObject(answer) || typeof answer.origin !== 'string') {
    throw new Error('The answer to the write held no placement');
  }
  return answer as unknown as IRankingPlacement;
}

function historyOf(answer: unknown): IRankingHistory {
  if (
    !isObject(answer) ||
    !Array.isArray(answer.items) ||
    !(answer.placement === null || isObject(answer.placement))
  ) {
    throw new Error('The answer held no placement history');
  }
  return answer as unknown as IRankingHistory;
}

function territoriesOf(answer: unknown): IParticipantTerritories {
  if (!isObject(answer) || !isObject(answer.office) || !Array.isArray(answer.territories)) {
    throw new Error('The answer held no territories');
  }
  return answer as unknown as IParticipantTerritories;
}

function territoriesPath(participantId: string): string {
  return `${BASE}/participants/${segment(participantId)}/ranking-territories`;
}

function rankingsPath(participantId: string): string {
  return `${BASE}/participants/${segment(participantId)}/rankings`;
}

function placementPath(participantId: string, address: IRankingAddress): string {
  const { surveyCode, list, year } = address;
  return [rankingsPath(participantId), ...[surveyCode, list, String(year)].map(segment)].join('/');
}

export const rankingsApi = {
  /**
   * The lists of the survey `surveyCode`'s placements are kept under, in the catalogue's order,
   * and that survey (`internal_fields.read`, staff and admin) — shared per code for the page load.
   * `null`, from a 404, for a code survey-api does not know.
   */
  lists(surveyCode: string): Promise<Nullable<IRankingLists>> {
    const held = listReads.get(surveyCode);
    if (held !== undefined) return held;
    const started = Promise.resolve()
      .then(() => apiRequest<unknown>(`${BASE}/surveys/${segment(surveyCode)}/ranking-lists`))
      .then(listsOf, (error: unknown) => {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      });
    listReads.set(surveyCode, started);
    started.catch(() => {
      if (listReads.get(surveyCode) === started) listReads.delete(surveyCode);
    });
    return started;
  },

  /**
   * A participant's placements in one survey (`internal_fields.read`) — those of the survey they
   * are kept under, `CANADA`'s for `CANADA_FR`: newest year first, then the catalogue's order
   * of lists; a removed one listed with a null `place`. Throws for a bodiless **404** — an
   * unknown participant, and nothing else (survey-api's D3) — and for a 400 `invalid-query` at
   * `survey`, a code that is none of survey-api's surveys.
   */
  placements(
    participantId: string,
    surveyCode: string,
    signal?: AbortSignal
  ): Promise<IRankingPlacement[]> {
    return apiRequest<unknown>(
      `${rankingsPath(participantId)}?survey=${encodeURIComponent(surveyCode)}`,
      { signal }
    ).then(placementsOf);
  },

  /**
   * Sets one placement (`internal_fields.write`) — 200 with the placement as the list draws it,
   * re-read, so it carries the actor's email. Creates the year's ranking where it has none, named
   * "<year> <label>"; an existing ranking keeps its name. The same place and territory on a staff
   * row writes nothing. `territory` is left out when empty. Refusals: 400
   * `invalid-body` at `reason` (blank, SR-507), `place` (missing, below 1) or `territory` (missing on a territorial list,
   * present on a national one, over 255 characters), the binder's 400 at `$.place`, 400
   * `invalid-query` at `year` (not a whole number from 2000 to 2100), a bodiless 404 (no
   * participant or survey, or a list the survey's catalogue lacks). `address.surveyCode` is the
   * catalogue's `keptUnder`.
   */
  set(
    participantId: string,
    address: IRankingAddress,
    body: ISetPlacementBody
  ): Promise<IRankingPlacement> {
    const sent: ISetPlacementBody = { place: body.place, reason: body.reason };
    if (body.territory) sent.territory = body.territory;
    return apiRequest<unknown>(placementPath(participantId, address), {
      method: 'PUT',
      body: sent
    }).then(placementOf);
  },

  /** Removes one placement — **204 whether or not a place was there**; the row stays with a null
   *  place and territory, so no import brings it back. The reason travels as `?reason=` and is
   *  required (SR-507: a blank one is 400 `invalid-query`). The 404 and the `year` 400 as `set`'s. */
  remove(participantId: string, address: IRankingAddress, reason: string): Promise<void> {
    return apiRequest<null>(`${placementPath(participantId, address)}?reason=${encodeURIComponent(reason)}`, {
      method: 'DELETE'
    }).then(() => undefined);
  },

  /** A participant's territories on the survey's territorial lists (`internal_fields.read`, SR-319)
   *  — `surveyCode` the catalogue's `keptUnder`. A bodiless 404 for an unknown participant. */
  territories(
    participantId: string,
    surveyCode: string,
    signal?: AbortSignal
  ): Promise<IParticipantTerritories> {
    return apiRequest<unknown>(
      `${territoriesPath(participantId)}?survey=${encodeURIComponent(surveyCode)}`,
      { signal }
    ).then(territoriesOf);
  },

  /** Sets staff's territory for one list (`rankings.manage`) — a live territory of the list's
   *  vocabulary and a reason, else 400 `invalid-body` at `territory` or `reason`. */
  setTerritory(
    participantId: string,
    surveyCode: string,
    list: string,
    territory: string,
    reason: string
  ): Promise<void> {
    return apiRequest<unknown>(
      `${territoriesPath(participantId)}/${segment(surveyCode)}/${segment(list)}`,
      { method: 'PUT', body: { territory, reason } }
    ).then(() => undefined);
  },

  /** Removes staff's territory for one list, the office's derivation in force again — 204 whether
   *  or not one stood; the reason travels as `?reason=` and is required. */
  clearTerritory(
    participantId: string,
    surveyCode: string,
    list: string,
    reason: string
  ): Promise<void> {
    return apiRequest<null>(
      `${territoriesPath(participantId)}/${segment(surveyCode)}/${segment(list)}?reason=${encodeURIComponent(reason)}`,
      { method: 'DELETE' }
    ).then(() => undefined);
  },

  /** A page of staff's territory overrides for one list (`internal_fields.read`, SR-319), newest
   *  first, ten a page; a bodiless 404 as `setTerritory`'s. */
  territoryHistory(
    participantId: string,
    surveyCode: string,
    list: string,
    page: number,
    signal?: AbortSignal
  ): Promise<ITerritoryOverrideHistory> {
    return apiRequest<unknown>(
      `${territoriesPath(participantId)}/${segment(surveyCode)}/${segment(list)}/events?page=${page}&pageSize=${HISTORY_PAGE_SIZE}`,
      { signal }
    ).then((answer) => {
      if (!isObject(answer) || !Array.isArray(answer.items)) {
        throw new Error('The answer held no events');
      }
      return answer as unknown as ITerritoryOverrideHistory;
    });
  },

  /** One placement's history: the placement as it is now — removed or not, or null where no row
   *  holds the address (a 200) — and a page of its events, newest first, ten a page. A bodiless
   *  404 as `set`'s. */
  history(
    participantId: string,
    address: IRankingAddress,
    page: number,
    signal?: AbortSignal
  ): Promise<IRankingHistory> {
    return apiRequest<unknown>(
      `${placementPath(participantId, address)}/events?page=${page}&pageSize=${HISTORY_PAGE_SIZE}`,
      { signal }
    ).then(historyOf);
  }
};
